import { useMemo, useState } from "react";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import clinicAPI from "../../../services/clinicApi";
import { showPortalInviteFeedback } from "../../../utils/portalInviteFeedback";
import {
  hasPortalLoginContact,
  portalEmailForEdit,
} from "./patientConstants";
import s from "../clinic.module.css";
import { Button, Field, Input, PhoneInput, useToast } from "../../ui";

export default function ClientPortalAccessSection({
  patientId,
  patient,
  edit,
  setEdit,
  subdomain,
  newPassword,
  setNewPassword,
  onSaveEmail,
  onSetPassword,
  savingEmail = false,
  settingPassword = false,
  inFold = false,
  childAccount = false,
}) {
  const toast = useToast();
  const [inviteUrl, setInviteUrl] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const hasLogin = hasPortalLoginContact(edit);
  const isActivated = Boolean(patient?.is_activated);
  const loginUrl = useMemo(
    () => (subdomain ? buildClinicPortalUrl(subdomain, "/login") : ""),
    [subdomain],
  );
  const displayEmail = portalEmailForEdit(edit.email);

  const copyText = async (text, okMsg) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMsg || "Скопировано");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const sendInvite = async () => {
    if (!patientId) return;
    setSendingInvite(true);
    try {
      const r = await clinicAPI.sendClientPortalInvite(patientId);
      const feedback = await showPortalInviteFeedback(
        toast,
        {
          invite_url: r.invite_url,
          invite_email_sent: r.email_sent,
          invite_sms_sent: r.sms_sent,
          message: r.message,
        },
        "Приглашение отправлено",
      );
      if (feedback.inviteUrl) setInviteUrl(feedback.inviteUrl);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось отправить приглашение");
    } finally {
      setSendingInvite(false);
    }
  };

  const rootClass = inFold
    ? `${s.clientPortalAccess} ${s.clientPortalAccessInFold}`
    : `${s.clientPortalAccess} ${s.clientProfileEmbeddedBlock}`;

  return (
    <div className={rootClass}>
      {!inFold ? (
        <div className={s.clientProfileEmbeddedHead}>
          <h3 className={s.clientTabCardTitle}>
            {childAccount ? "Кабинет ребёнка" : "Доступ в личный кабинет"}
          </h3>
          <span className={s.clientPortalAccessStatus}>
            {!hasLogin
              ? "Нужен email"
              : isActivated
                ? "Активирован"
                : "Ожидает активации"}
          </span>
        </div>
      ) : null}

      {!hasLogin ? (
        <div className={`${s.patientAlertBanner} ${s.clientPortalAccessBanner}`}>
          {childAccount
            ? "Укажите email ребёнка — на него придёт ссылка для входа в личный кабинет спортсмена. Родитель управляет семьёй из своего аккаунта."
            : "Укажите email клиента или родителя — на него придёт ссылка для активации личного кабинета. Телефон необязателен."}
        </div>
      ) : !isActivated ? (
        <div className={`${s.patientAlertBanner} ${s.clientPortalAccessBanner}`}>
          Аккаунт создан, но пароль ещё не задан. Отправьте приглашение на email
          или скопируйте ссылку — она действует 7 суток.
        </div>
      ) : null}

      {hasLogin ? (
        <section className={s.clientPortalAccessCard}>
          <h4 className={s.clientPortalAccessCardTitle}>Приглашение</h4>
          <p className={s.clientPortalAccessCardHint}>
            {childAccount
              ? "Ребёнок входит со своего email. Родителя приглашают отдельно из карточки родителя."
              : "Ссылка для входа по email и паролю"}
          </p>
          <div className={s.clientPortalAccessActions}>
            <Button
              type="button"
              onClick={sendInvite}
              disabled={sendingInvite || !patientId}
            >
              {sendingInvite
                ? "Отправка…"
                : isActivated
                  ? "Отправить ссылку снова"
                  : "Отправить приглашение"}
            </Button>
          </div>
          {inviteUrl ? (
            <Field label="Ссылка приглашения">
              <div className={s.clientPortalAccessFieldRow}>
                <Input value={inviteUrl} readOnly />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => copyText(inviteUrl, "Ссылка скопирована")}
                >
                  Копировать
                </Button>
              </div>
            </Field>
          ) : null}
          {loginUrl && isActivated ? (
            <Field label="Страница входа">
              <div className={s.clientPortalAccessFieldRow}>
                <Input value={loginUrl} readOnly />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => copyText(loginUrl, "Ссылка скопирована")}
                >
                  Копировать
                </Button>
              </div>
            </Field>
          ) : null}
        </section>
      ) : null}

      <section className={s.clientPortalAccessCard}>
        <h4 className={s.clientPortalAccessCardTitle}>Контакты для входа</h4>
        <form
          className={s.clientPortalAccessForm}
          onSubmit={(e) => {
            e.preventDefault();
            onSaveEmail?.(e);
          }}
        >
          <Field
            label="Email"
            hint="Основной способ входа и email-приглашения"
          >
            <Input
              type="email"
              value={displayEmail}
              onChange={(e) =>
                setEdit({
                  ...edit,
                  email: e.target.value.trim(),
                })
              }
              placeholder="client@example.com"
              autoComplete="off"
            />
          </Field>
          <Field label="Телефон" hint="Для связи и уведомлений (необязательно)">
            <PhoneInput
              value={edit.phone}
              onChange={(phone) => setEdit({ ...edit, phone })}
              autoComplete="off"
            />
          </Field>
          <div className={s.clientPortalAccessFormActions}>
            <Button type="submit" size="sm" disabled={savingEmail || !patientId}>
              {savingEmail ? "Сохранение…" : "Сохранить контакты"}
            </Button>
          </div>
        </form>
      </section>

      <section className={s.clientPortalAccessCard}>
        <h4 className={s.clientPortalAccessCardTitle}>Пароль вручную</h4>
        <p className={s.clientPortalAccessCardHint}>
          Минимум 8 символов — если нужно задать без ссылки
        </p>
        <div className={s.clientPortalAccessFieldRow}>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Новый пароль"
            autoComplete="new-password"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onSetPassword}
            disabled={settingPassword || !patientId}
          >
            {settingPassword ? "Сохранение…" : "Задать пароль"}
          </Button>
        </div>
      </section>
    </div>
  );
}
